"use client";

import { LayoutDashboard, BarChart3, Users, Shield, Save } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ActionsSection } from "@/components/landing/demo-report-details/actions-section";
import { CustomersSection } from "@/components/landing/demo-report-details/customers-section";
import { OverviewSection } from "@/components/landing/demo-report-details/overview-section";
import { PainPointsSection } from "@/components/landing/demo-report-details/pain-points-section";
import { ValidationSection } from "@/components/landing/demo-report-details/validation-section";
import { useDemoPersistence } from "@/src/features/demo-contracts/demo-mode-persistence";
import { deriveValidatorScore } from "@/src/features/demo-contracts/score-derivation";
import {
  DEMO_DETAIL_TABS,
  type DemoDetailTab,
  type DemoPreview,
} from "@/src/features/demo-contracts/types";

export interface DemoReportDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: DemoPreview;
}

export function DemoReportDetailsModal({
  open,
  onOpenChange,
  preview,
}: DemoReportDetailsModalProps) {
  const {
    interactionState,
    togglePainPointSaved,
    clearSavedPainPoints,
    setActiveTab,
    setSelectedIdea,
    setMomTestAnswer,
  } = useDemoPersistence(preview.nicheSlug);

  const activeTab = interactionState.lastActiveTab ?? DEMO_DETAIL_TABS.OVERVIEW;

  // Derive validator score with Mom Test answers factored in.
  const effectiveScore =
    interactionState.momTestAnswers &&
    Object.keys(interactionState.momTestAnswers).length > 0
      ? deriveValidatorScore(preview, {
          momTestAnswers: interactionState.momTestAnswers,
        })
      : preview.validatorContext?.score;

  const effectivePreview = effectiveScore
    ? {
        ...preview,
        validatorContext: {
          score: effectiveScore,
          derivedAt: new Date().toISOString(),
        },
      }
    : preview;

  const tabMeta = [
    {
      value: DEMO_DETAIL_TABS.OVERVIEW,
      icon: LayoutDashboard,
      label: "Overview",
      shortLabel: "Overview",
    },
    {
      value: DEMO_DETAIL_TABS.PAIN_POINTS,
      icon: BarChart3,
      label: "Pain Points",
      shortLabel: "Signals",
    },
    {
      value: DEMO_DETAIL_TABS.CUSTOMERS,
      icon: Users,
      label: "Customers",
      shortLabel: "Audience",
    },
    {
      value: DEMO_DETAIL_TABS.VALIDATION,
      icon: Shield,
      label: "Validation",
      shortLabel: "Score",
    },
    {
      value: DEMO_DETAIL_TABS.ACTIONS,
      icon: Save,
      label: "Actions",
      shortLabel: "Actions",
    },
  ] as const;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[96vh] w-[min(96vw,1040px)] overflow-hidden border-white/15 bg-[linear-gradient(180deg,rgba(21,17,14,0.98),rgba(13,16,21,0.98))] p-0 text-primary-foreground shadow-2xl shadow-black/40 backdrop-blur-sm">
        <DialogHeader className="border-b border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] px-4 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-accent/75">
                Evidence console
              </p>
              <DialogTitle className="font-serif text-[20px] font-semibold leading-tight text-primary-foreground sm:text-[22px]">
                Validation report
              </DialogTitle>
              <DialogDescription className="mt-1 max-w-2xl font-mono text-[12px] leading-[1.7] text-primary-foreground/80 sm:text-[13px]">
                Review what people are struggling with, who the pain belongs to,
                how strong the signal is, and what to do next.
              </DialogDescription>
            </div>
            <span className="shrink-0 rounded-md border border-accent/25 bg-accent/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-accent/85">
              Demo mode
            </span>
          </div>
          <p className="mt-2 max-w-2xl font-mono text-[11px] leading-[1.7] text-primary-foreground/60">
            Saved selections stay in your browser only. This preview is designed
            for exploration, not account setup.
          </p>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as DemoDetailTab)}
          className="flex flex-col"
        >
          <div className="border-b border-white/10 px-4 py-3 sm:px-6">
            <TabsList className="grid w-full grid-cols-4 gap-1.5 bg-white/[0.04] p-1 sm:grid-cols-5 sm:gap-1">
              {tabMeta.map((tab) => {
                const Icon = tab.icon;

                return (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="min-h-11 gap-1.5 border border-transparent px-2 py-2.5 font-mono text-[10px] uppercase tracking-widest text-primary-foreground/78 data-[state=active]:border-white/10 data-[state=active]:bg-white/[0.08] data-[state=active]:text-primary-foreground sm:min-h-0 sm:gap-1.5 sm:px-3 sm:py-2"
                  >
                    <Icon className="size-3.5" aria-hidden="true" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.shortLabel}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          <ScrollArea className="h-[calc(96vh-140px)] px-4 py-5 sm:px-6 md:h-[70vh]">
            <TabsContent value={DEMO_DETAIL_TABS.OVERVIEW} className="mt-0">
              <OverviewSection
                preview={effectivePreview}
                selectedIdeaId={interactionState.selectedIdeaId}
                onSelectIdea={setSelectedIdea}
              />
            </TabsContent>

            <TabsContent value={DEMO_DETAIL_TABS.PAIN_POINTS} className="mt-0">
              <PainPointsSection
                preview={effectivePreview}
                savedPainPointIds={interactionState.savedPainPointIds}
                onToggleSaved={togglePainPointSaved}
                onClearSaved={clearSavedPainPoints}
              />
            </TabsContent>

            <TabsContent value={DEMO_DETAIL_TABS.CUSTOMERS} className="mt-0">
              <CustomersSection preview={effectivePreview} />
            </TabsContent>

            <TabsContent value={DEMO_DETAIL_TABS.VALIDATION} className="mt-0">
              <ValidationSection
                preview={effectivePreview}
                momTestAnswers={interactionState.momTestAnswers ?? {}}
                onMomTestAnswer={setMomTestAnswer}
              />
            </TabsContent>

            <TabsContent value={DEMO_DETAIL_TABS.ACTIONS} className="mt-0">
              <ActionsSection preview={effectivePreview} />
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
